import { Withdrawal } from "@apis/index";
import deliveryManApi from "@apis/deliveryman";
import Button from "@components/button";
import Row from "@components/row";
import Table from "@components/table";
import Title from "@components/title";
import { useModal } from "@context/ModalContext";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import {
  Wallet,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  History,
  Eye,
} from "lucide-react";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import Helper from "@utils/helper";

// ═══════════════════════════════════════
// Styled Components
// ═══════════════════════════════════════
const PageWrapper = styled.div`
  padding: 24px;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const WalletSection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const WalletCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 28px 32px;
  color: white;
  flex: 1;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -30%;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const WalletLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  opacity: 0.85;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WalletAmount = styled.div`
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const WalletSubtext = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-top: 8px;
`;

const WithdrawButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StatusPill = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${({ status }) => {
    switch (status) {
      case "Approved":
        return "rgba(16, 185, 129, 0.1)";
      case "Rejected":
        return "rgba(239, 68, 68, 0.1)";
      case "Pending":
        return "rgba(245, 158, 11, 0.1)";
      default:
        return "rgba(107, 114, 128, 0.1)";
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case "Approved":
        return "#059669";
      case "Rejected":
        return "#dc2626";
      case "Pending":
        return "#d97706";
      default:
        return "#6b7280";
    }
  }};
`;

const ModalOverlay = styled.div`
  padding: 28px;
  min-width: 380px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 24px 0;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }

  input {
    width: 100%;
    padding: 12px 16px;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 16px;
    transition: border-color 0.2s;
    outline: none;
    background: #f9fafb;

    &:focus {
      border-color: #667eea;
      background: white;
    }
  }

  .help {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 6px;
  }
`;

const BalanceDisplay = styled.div`
  background: linear-gradient(135deg, #f0f4ff 0%, #e8ecff 100%);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .label {
    font-size: 13px;
    color: #6b7280;
  }

  .amount {
    font-size: 18px;
    font-weight: 700;
    color: #667eea;
  }
`;

const AmountCell = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #1f2937;
`;

const DateCell = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "Approved":
      return <CheckCircle2 size={14} />;
    case "Rejected":
      return <XCircle size={14} />;
    case "Pending":
      return <Clock size={14} />;
    default:
      return null;
  }
};

const PayoutHistoryModal = ({
  item,
  onClose,
  settings,
}: {
  item: any;
  onClose: () => void;
  settings: any;
}) => {
  const { bucketUrl } = useConfig();
  const { showModal, hideModal } = useModal();
  const payouts = item.payouts || [];

  return (
    <ModalOverlay>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <ModalTitle style={{ marginBottom: 0 }}>Payout History</ModalTitle>
        <div
          style={{
            background: "#f3f4f6",
            padding: "4px 12px",
            borderRadius: "99px",
            fontSize: "12px",
            color: "#6b7280",
            fontWeight: 500,
          }}
        >
          #{item.id}
        </div>
      </div>

      <ModalSubtitle>
        Detailed breakdown of all partial payments received for this request.
      </ModalSubtitle>

      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          paddingRight: "8px",
          marginTop: "20px",
        }}
      >
        {payouts.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#9ca3af",
            }}
          >
            No payouts recorded yet.
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {payouts.map((payout: any, index: number) => (
              <div
                key={payout.id}
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", gap: "16px", alignItems: "center" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "#f0fdf4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#16a34a",
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircle2 size={20} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#111827",
                        fontSize: "15px",
                      }}
                    >
                      {Helper.formatCurrency(payout.amount, settings)}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {new Date(payout.createdAt).toLocaleString("en-US", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>

                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  {payout.receipt && (
                    <div
                      onClick={() =>
                        showModal(
                          <ViewReceiptModal
                            receipt={payout.receipt}
                            onClose={hideModal}
                          />,
                        )
                      }
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "1px solid #e5e7eb",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={`${bucketUrl}/${payout.receipt}`}
                        alt="Receipt"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        fontFamily: "monospace",
                      }}
                    >
                      {payout.transactionId || "No TXID"}
                    </div>
                    {payout.adminNote && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#6b7280",
                          fontStyle: "italic",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={payout.adminNote}
                      >
                        {payout.adminNote}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button title="Close" onClick={onClose} bg="grey500" />
      </div>
    </ModalOverlay>
  );
};

const ViewReceiptModal = ({
  receipt,
  onClose,
}: {
  receipt: string;
  onClose: () => void;
}) => {
  const { bucketUrl } = useConfig();
  return (
    <ModalOverlay style={{ padding: 0, overflow: "hidden", maxWidth: "90vw" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <ModalTitle style={{ margin: 0, fontSize: "16px" }}>
          Payment Receipt
        </ModalTitle>
        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <XCircle size={20} />
        </button>
      </div>
      <div
        style={{
          padding: "24px",
          display: "flex",
          justifyContent: "center",
          background: "#f9fafb",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <img
          src={`${bucketUrl}/${receipt}`}
          alt="Receipt"
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          }}
        />
      </div>
      <div
        style={{
          padding: "16px 24px",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          title="Download Receipt"
          onClick={() => window.open(`${bucketUrl}/${receipt}`, "_blank")}
        />
      </div>
    </ModalOverlay>
  );
};

// ═══════════════════════════════════════
// Request Modal
// ═══════════════════════════════════════
const RequestModal = ({
  balance,
  onClose,
  onSuccess,
  settings,
  targetUserId,
}: {
  balance: number;
  onClose: () => void;
  onSuccess: () => void;
  settings: any;
  targetUserId?: number;
}) => {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const val = Number(amount);
    if (!val || val <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (val > balance) {
      toast.error("Amount exceeds your wallet balance");
      return;
    }

    setLoading(true);
    try {
      await Withdrawal.create({
        data: { amount: val, userId: targetUserId },
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalTitle>Request Withdrawal</ModalTitle>
      <ModalSubtitle>
        Enter the amount you'd like to withdraw from your wallet.
      </ModalSubtitle>

      <BalanceDisplay>
        <span className="label">Available Balance</span>
        <span className="amount">
          {Helper.formatCurrency(balance, settings)}
        </span>
      </BalanceDisplay>

      <InputGroup>
        <label>Withdrawal Amount</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          min="1"
          max={balance}
        />
        <div className="help">
          Funds will be transferred to your registered bank account.
        </div>
      </InputGroup>

      <Row styleRow="justify-content: flex-end; gap: 10px;">
        <Button title="Cancel" onClick={onClose} bg="grey500" />
        <Button
          title={loading ? "Processing..." : "Submit Request"}
          onClick={handleSubmit}
        />
      </Row>
    </ModalOverlay>
  );
};

// ═══════════════════════════════════════
// Main Component
// ═══════════════════════════════════════
const DeliveryWithdrawals: React.FC<{ state: any }> = ({ state }) => {
  const deliveryManId = state?.id;
  const { showModal, hideModal } = useModal();
  const { settings } = useGeneralSettings();
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);

  // Pagination state (even though Table handles some, we need it for API)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  const fetchBalance = async () => {
    try {
      const res = await deliveryManApi.getAnalytics(deliveryManId);
      if (res && res.walletBalance !== undefined) {
        setBalance(res.walletBalance);
      }
    } catch (error) {
      console.error("Failed to fetch balance", error);
    }
  };

  const fetchWithdrawals = async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, limit: 10 };
      if (deliveryManId) {
        params.userId = deliveryManId;
      }
      const response = await Withdrawal.list({ params });
      setWithdrawals(response.items || []);
      setPagination({
        page: response.page,
        limit: 10,
        totalItems: response.totalItems,
        totalPages: response.totalPages,
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deliveryManId) {
      fetchBalance();
      fetchWithdrawals();
    }
  }, [deliveryManId]);

  const columns = [
    {
      title: "#",
      key: "id",
      render: (item: any) => (
        <span style={{ fontWeight: 600, color: "#6b7280" }}>#{item.id}</span>
      ),
    },
    {
      title: "Amount (Paid / Total)",
      key: "amount",
      render: (item: any) => (
        <AmountCell style={{ fontSize: "14px" }}>
          <div>
            <span style={{ color: "#10b981", fontWeight: 700 }}>
              {Helper.formatCurrency(item.paidAmount || 0, settings)}
            </span>
            <span style={{ color: "#9ca3af", margin: "0 4px" }}>/</span>
            <span style={{ color: "#6b7280" }}>
              {Helper.formatCurrency(item.amount, settings)}
            </span>
          </div>
          {item.payouts && item.payouts.length > 0 && (
            <div
              onClick={() =>
                showModal(
                  <PayoutHistoryModal
                    item={item}
                    onClose={hideModal}
                    settings={settings}
                  />,
                )
              }
              style={{
                fontSize: "11px",
                color: "#6366f1",
                cursor: "pointer",
                marginTop: "4px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              <History size={12} />
              View History
            </div>
          )}
        </AmountCell>
      ),
    },
    {
      title: "Date",
      key: "createdAt",
      render: (item: any) => (
        <DateCell>
          {new Date(item.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </DateCell>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (item: any) => {
        const isPartiallyPaid =
          item.status === "Pending" && (item.paidAmount || 0) > 0;
        const isPartiallyPaidAndRejected =
          item.status === "Rejected" && (item.paidAmount || 0) > 0;

        let displayStatus = item.status;
        let pillStatus = item.status;

        if (isPartiallyPaid) {
          displayStatus = "Partially Paid";
          pillStatus = "Approved";
        } else if (isPartiallyPaidAndRejected) {
          displayStatus = "Partially Paid & Rejected";
          pillStatus = "Rejected";
        }

        return (
          <StatusPill status={pillStatus}>
            <StatusIcon status={pillStatus} />
            {displayStatus}
          </StatusPill>
        );
      },
    },
    {
      title: "Receipt",
      key: "receipt",
      render: (item: any) =>
        item.receipt ? (
          <div style={{ display: "flex" }}>
            <div
              onClick={() =>
                showModal(
                  <ViewReceiptModal
                    receipt={item.receipt}
                    onClose={hideModal}
                  />,
                )
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#6366f1",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                padding: "6px 10px",
                background: "rgba(99, 102, 241, 0.1)",
                borderRadius: "8px",
              }}
            >
              <Eye size={14} /> View
            </div>
          </div>
        ) : (
          <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>
        ),
    },
  ];

  return (
    <PageWrapper>
      <HeaderArea>
        <Title title="Withdrawals" />
        <WithdrawButton
          onClick={() =>
            showModal(
              <RequestModal
                balance={balance}
                onClose={hideModal}
                onSuccess={() => {
                  fetchBalance();
                  fetchWithdrawals();
                }}
                settings={settings}
                targetUserId={deliveryManId}
              />,
            )
          }
        >
          <ArrowUpRight size={18} />
          Request Withdrawal
        </WithdrawButton>
      </HeaderArea>

      <WalletSection>
        <WalletCard>
          <WalletLabel>
            <Wallet size={14} /> Total Wallet Balance
          </WalletLabel>
          <WalletAmount>
            {Helper.formatCurrency(balance, settings)}
          </WalletAmount>
          <WalletSubtext>Available for withdrawal</WalletSubtext>
        </WalletCard>
        {/* Can add more cards if needed, like Total Withdrawn, Pending, etc. analogous to dashboard */}
      </WalletSection>

      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <History size={20} color="#6b7280" />
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#111827",
              margin: 0,
            }}
          >
            History
          </h3>
        </div>
        <Table
          columns={columns}
          data={withdrawals}
          loading={loading}
          pagination={{ ...pagination, onPageChange: fetchWithdrawals }}
        />
      </div>
    </PageWrapper>
  );
};

export default DeliveryWithdrawals;
